import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { invoiceTemplatesApi } from '../../api/invoiceTemplates';
import LoadingSpinner from '../../components/LoadingSpinner';

// Muted professional palette (lighter than the near-black navy, but no candy
// tones): steel blue, teal, forest, burgundy, plum, slate + the brand navy.
const ACCENT_SWATCHES = ['#2C5F8A', '#0F766E', '#3A5F4A', '#7C2D3E', '#5B4B8A', '#475569', '#071E3B', '#78716C'];
const TITLE_GREY = '#374151';
const MODERN_DEFAULT_ACCENT = '#2C5F8A';

/**
 * WYSIWYG template canvas: the WHOLE sample invoice is the editing surface.
 * Click the logo slot to upload, the title/grand-total for the accent color,
 * the Mat. vnt. header to toggle the column, the payment block to emphasize,
 * the footer line to type. Every click autosaves; "Atidaryti PDF" opens the
 * authoritative QuestPDF render. The canvas mirrors the PDF structure but need
 * not be pixel-identical - the PDF is the truth.
 */
export default function InvoiceTemplateBuilder() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const savedTimer = useRef<number | null>(null);

  const { data: template, isLoading } = useQuery({
    queryKey: ['invoice-template'],
    queryFn: invoiceTemplatesApi.get,
  });

  const [accent, setAccent] = useState<string | null>(null);
  const [preset, setPreset] = useState('classic');
  const [showUnitColumn, setShowUnitColumn] = useState(true);
  const [emphasizePaymentDetails, setEmphasizePaymentDetails] = useState(true);
  const [footerText, setFooterText] = useState('');
  const [hasLogo, setHasLogo] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [footerEditing, setFooterEditing] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useMemo(() => {
    if (template && !initialized) {
      setAccent(template.accentColor);
      setPreset(template.preset ?? 'classic');
      setShowUnitColumn(template.showUnitColumn);
      setEmphasizePaymentDetails(template.emphasizePaymentDetails);
      setFooterText(template.footerText ?? '');
      setHasLogo(template.hasLogo);
      setInitialized(true);
    }
  }, [template, initialized]);

  // Refetch after every upload: the server stores the background-REMOVED PNG
  // (rembg sidecar) which differs from the optimistic local preview.
  useEffect(() => {
    if (!hasLogo) return;
    let url: string | null = null;
    invoiceTemplatesApi.logoUrl().then(u => { url = u; setLogoSrc(u); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [hasLogo, logoVersion]);

  const flashSaved = () => {
    setSavedFlash(true);
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
  };

  const saveMutation = useMutation({
    mutationFn: (payload: { accent: string | null; preset: string; showUnit: boolean; emphasize: boolean; footer: string }) =>
      invoiceTemplatesApi.save({
        accentColor: payload.accent,
        preset: payload.preset,
        showUnitColumn: payload.showUnit,
        emphasizePaymentDetails: payload.emphasize,
        footerText: payload.footer,
      }),
    onSuccess: () => { flashSaved(); void queryClient.invalidateQueries({ queryKey: ['invoice-template'] }); },
  });

  // Click IS the save: every canvas interaction persists immediately.
  const apply = (patch: Partial<{ accent: string | null; preset: string; showUnit: boolean; emphasize: boolean; footer: string }>) => {
    const next = {
      accent: patch.accent !== undefined ? patch.accent : accent,
      preset: patch.preset !== undefined ? patch.preset : preset,
      showUnit: patch.showUnit !== undefined ? patch.showUnit : showUnitColumn,
      emphasize: patch.emphasize !== undefined ? patch.emphasize : emphasizePaymentDetails,
      footer: patch.footer !== undefined ? patch.footer : footerText,
    };
    if (patch.accent !== undefined) setAccent(patch.accent);
    if (patch.preset !== undefined) setPreset(patch.preset);
    if (patch.showUnit !== undefined) setShowUnitColumn(patch.showUnit);
    if (patch.emphasize !== undefined) setEmphasizePaymentDetails(patch.emphasize);
    if (patch.footer !== undefined) setFooterText(patch.footer);
    saveMutation.mutate(next);
  };

  const uploadMutation = useMutation({
    mutationFn: (file: File) => invoiceTemplatesApi.uploadLogo(file),
    onSuccess: async () => {
      setHasLogo(true);
      setLogoVersion(v => v + 1);
      flashSaved();
      await queryClient.invalidateQueries({ queryKey: ['invoice-template'] });
    },
  });

  const removeLogoMutation = useMutation({
    mutationFn: invoiceTemplatesApi.removeLogo,
    onSuccess: async () => {
      setHasLogo(false);
      setLogoSrc(null);
      flashSaved();
      await queryClient.invalidateQueries({ queryKey: ['invoice-template'] });
    },
  });

  const handleLogoPick = (files: FileList | null) => {
    const file = files?.[0];
    if (file) {
      const localUrl = URL.createObjectURL(file);
      setLogoSrc(localUrl);
      uploadMutation.mutate(file);
    }
  };

  const handlePreview = async () => {
    const tab = window.open('', '_blank');
    try {
      const url = await invoiceTemplatesApi.previewUrl();
      if (tab) tab.location.href = url;
    } catch (err) {
      tab?.close();
    }
  };

  const commitFooter = () => {
    setFooterEditing(false);
    apply({ footer: footerText });
  };

  if (isLoading) return <LoadingSpinner />;

  // Modern defaults to the lighter blue when no accent is chosen (the PDF
  // composer uses the same fallback); classic/minimal keep the stock grey title.
  const accentColor = accent ?? (preset === 'modern' ? MODERN_DEFAULT_ACCENT : TITLE_GREY);

  const sampleLines = [
    { name: 'Konsultacinės paslaugos', unit: 'val.', qty: '10,00', price: '20,00', vat: '21%', sum: '200,00', sumVat: '42,00' },
    { name: 'Prekės pavadinimas', unit: 'vnt.', qty: '2,00', price: '50,00', vat: '21%', sum: '100,00', sumVat: '21,00' },
  ];

  return (
    <div className="max-w-5xl space-y-3">
      <div className="flex flex-wrap items-center gap-3 py-2 border-b border-gray-100">
        <h1 className="text-xl font-semibold text-gray-900">{t('invoiceTemplate.title')}</h1>
        <span className="text-xs text-gray-400">{t('invoiceTemplate.canvasHint')}</span>
        <div className="ml-auto flex items-center gap-3">
          {savedFlash && <span className="text-xs text-green-600">{t('invoiceTemplate.saved')}</span>}
          <button
            type="button"
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            onClick={handlePreview}
          >
            {t('invoiceTemplate.preview')}
          </button>
        </div>
      </div>

      {/* Preset chooser (left) + accent palette (right, always visible -
          click a swatch to apply, ∅ to go accentless; every click autosaves). */}
      <div className="flex flex-wrap items-center gap-2">
        {(['classic', 'minimal', 'modern'] as const).map(p => (
          <button
            key={p}
            type="button"
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              preset === p
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
            }`}
            onClick={() => apply({ preset: p })}
          >
            {t(`invoiceTemplate.preset_${p}`)}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1.5">
          {ACCENT_SWATCHES.map(sw => (
            <button
              key={sw}
              type="button"
              className={`h-6 w-6 rounded-full border transition-transform hover:scale-110 ${
                accent === sw ? 'border-gray-900 ring-2 ring-gray-300 scale-110' : 'border-gray-300'
              }`}
              style={{ backgroundColor: sw }}
              onClick={() => apply({ accent: sw })}
              title={sw}
            />
          ))}
          <button
            type="button"
            className="h-6 w-6 rounded-full border border-dashed border-gray-400 text-gray-400 text-xs leading-none hover:text-gray-600"
            onClick={() => apply({ accent: null })}
            title={t('invoiceTemplate.accentNone')}
          >
            ∅
          </button>
        </div>
      </div>

      {/* The invoice itself — a REAL-size A4 sheet (210×297mm, 40pt margins, pt
          typography copied from the QuestPDF layout), uniformly scaled to fit.
          Because every size below is the exact PDF value in pt/mm, the canvas and
          the printed PDF are the same document at different zoom levels. */}
      <div className="bg-gray-200/70 rounded-xl p-4 md:p-8 overflow-x-auto">
        <div
          className="bg-white shadow-xl rounded-sm mx-auto relative flex flex-col"
          style={{
            width: '210mm',
            minWidth: '210mm',
            minHeight: '297mm',
            padding: '40pt',
            fontSize: '10pt',
            // Same family + line-height as the QuestPDF page default (bundled Lato).
            fontFamily: "'Lato', 'Segoe UI', system-ui, sans-serif",
            lineHeight: 1.25,
            zoom: 0.78,
          }}
        >
          {preset === 'modern' && (
            <div className="absolute top-0 left-0 right-0" style={{ height: 6, backgroundColor: accentColor }} />
          )}
        {/* Header per preset - all sizes mirror the QuestPDF compose branches.
            classic: title left / logo right. minimal: centered compact (logo in
            the footer). modern: full accent band with logo + white title left,
            white meta right. */}
        {preset === 'minimal' ? (
          <div className="text-center" style={{ paddingBottom: '12pt' }}>
            <h2
              className="cursor-pointer hover:opacity-80"
              style={{ color: accentColor, fontSize: '14pt', fontWeight: 600 }}
              title={t('invoiceTemplate.accentColor')}
            >
              SĄSKAITA FAKTŪRA
            </h2>
            <div className="text-gray-500" style={{ fontSize: '9pt', marginTop: '2pt' }}>
              SF Nr.: SF000001&nbsp;&nbsp;·&nbsp;&nbsp;{new Date().toISOString().slice(0, 10)}&nbsp;&nbsp;·&nbsp;&nbsp;EUR
            </div>
          </div>
        ) : preset === 'modern' ? (
          <div style={{ marginBottom: '8pt' }}>
            {/* Accent bar + classic header - the solid band was too heavy. */}
            <div style={{ height: '8pt', backgroundColor: accentColor }} />
            <div className="flex justify-between items-start relative" style={{ paddingTop: '10pt', paddingBottom: '8pt' }}>
              <div className="relative">
                <h2
                  className="cursor-pointer hover:opacity-80"
                  style={{ color: accentColor, fontSize: '20pt', fontWeight: 700, lineHeight: 1.2 }}
                  title={t('invoiceTemplate.accentColor')}
                >
                  SĄSKAITA FAKTŪRA
                </h2>
              </div>
              <div className="text-right">
                <div className="group relative inline-block">
                  {hasLogo && logoSrc ? (
                    <img src={logoSrc} alt="logo" className="inline-block" style={{ height: '48pt' }} />
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded text-gray-400 hover:border-primary-400 hover:text-primary-500"
                      style={{ height: '48pt', width: '150pt', fontSize: '10pt' }}
                      title={t('invoiceTemplate.logoUpload')}
                    >
                      {uploadMutation.isPending ? t('invoiceTemplate.uploading') : t('invoiceTemplate.logoUpload')}
                    </button>
                  )}
                  {hasLogo && (
                    <button
                      type="button"
                      className="hidden group-hover:block absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white text-xs leading-none"
                      onClick={() => removeLogoMutation.mutate()}
                      title={t('invoiceTemplate.logoRemove')}
                    >
                      ✕
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={e => { handleLogoPick(e.target.files); e.target.value = ''; }}
                  />
                </div>
                <div style={{ fontSize: '10pt', marginTop: '2pt' }}>
                  <div className="font-semibold" style={{ fontSize: '12pt' }}>SF Nr.: SF000001</div>
                  <div className="text-gray-500">Data: {new Date().toISOString().slice(0, 10)}</div>
                  <div className="text-gray-500">Valiuta: EUR</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-start relative" style={{ paddingBottom: '8pt' }}>
            <div className="relative">
              <h2
                className="cursor-pointer hover:opacity-80"
                style={{ color: accentColor, fontSize: '20pt', fontWeight: 700, lineHeight: 1.2 }}
                title={t('invoiceTemplate.accentColor')}
              >
                SĄSKAITA FAKTŪRA
              </h2>
            </div>

            <div className="text-right">
              {/* Logo slot: click to add, hover ✕ to remove. 48pt = the PDF value. */}
              <div className="group relative inline-block">
                {hasLogo && logoSrc ? (
                  <img src={logoSrc} alt="logo" className="inline-block" style={{ height: '48pt' }} />
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded text-gray-400 hover:border-primary-400 hover:text-primary-500"
                    style={{ height: '48pt', width: '150pt', fontSize: '10pt' }}
                    title={t('invoiceTemplate.logoUpload')}
                  >
                    {uploadMutation.isPending ? t('invoiceTemplate.uploading') : t('invoiceTemplate.logoUpload')}
                  </button>
                )}
                {hasLogo && (
                  <button
                    type="button"
                    className="hidden group-hover:block absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white text-xs leading-none"
                    onClick={() => removeLogoMutation.mutate()}
                    title={t('invoiceTemplate.logoRemove')}
                  >
                    ✕
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={e => { handleLogoPick(e.target.files); e.target.value = ''; }}
                />
              </div>
              <div style={{ fontSize: '10pt', marginTop: '2pt' }}>
                <div className="font-semibold" style={{ fontSize: '12pt' }}>SF Nr.: SF000001</div>
                <div className="text-gray-500">Data: {new Date().toISOString().slice(0, 10)}</div>
                <div className="text-gray-500">Valiuta: EUR</div>
              </div>
            </div>
          </div>
        )}

        {/* Seller / buyer */}
        {/* Seller / buyer — PDF values: labels 10pt semi-bold grey, names 11pt
            semi-bold, details 10pt grey, payment block 11pt semi-bold pad-top 4pt. */}
        <div className="grid grid-cols-2 gap-6 mb-5" style={{ paddingTop: '8pt' }}>
          <div>
            <div className="text-gray-500 font-semibold mb-0.5">PARDAVĖJAS</div>
            <div className="font-semibold" style={{ fontSize: '11pt' }}>UAB Pavyzdys</div>
            <div className="text-gray-500">Įmonės kodas: 123456789</div>
            <div className="text-gray-500">Gatvė 1, Vilnius</div>
            <div
              className={`cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 ${emphasizePaymentDetails ? 'font-semibold' : 'text-gray-500'}`}
              style={{ fontSize: emphasizePaymentDetails ? '11pt' : '10pt', marginTop: '4pt' }}
              onClick={() => apply({ emphasize: !emphasizePaymentDetails })}
              title={t('invoiceTemplate.emphasizePayment')}
            >
              Apmokėjimo rekvizitai: LT00 0000 0000 0000
            </div>
          </div>
          <div style={preset === 'modern' ? { backgroundColor: '#E8EDF4', padding: '10pt' } : undefined}>
            <div className="text-gray-500 font-semibold mb-0.5">PIRKĖJAS</div>
            <div className="font-semibold" style={{ fontSize: '11pt' }}>UAB Klientas</div>
            <div className="text-gray-500">Įmonės kodas: 987654321</div>
            <div className="text-gray-500">Prospectas 2, Kaunas</div>
          </div>
        </div>

        <div className="text-gray-600" style={{ paddingBottom: '10pt' }}>Turinys / aprašymas: Paslaugos pagal 2026 m. rugsėjo mėn. sutartį</div>

        {/* Lines: the Mat. vnt. header is the toggle. Preset styles the header row.
            Cell padding = the QuestPDF PaddingVertical(5)/PaddingHorizontal(4) in pt;
            row rule 1pt grey; classic header = grey fill (PDF Grey.Lighten3). */}
        <table className="w-full" style={{ fontSize: '10pt', borderCollapse: 'collapse' }}>
          <thead>
            <tr className={
              preset === 'minimal'
                ? 'text-left'
                : preset === 'modern'
                  ? 'text-left'
                  : 'text-left'
            }
            style={{
              backgroundColor: preset === 'minimal' ? 'transparent' : preset === 'modern' ? accentColor : '#E0E0E0',
              color: preset === 'modern' ? '#FFFFFF' : undefined,
              borderBottom: preset === 'minimal' ? '1.5pt solid #616161' : undefined,
            }}>
              <th className="font-semibold text-left" style={{ padding: '5pt 4pt' }}>Eil. Nr.</th>
              <th className="font-semibold text-left" style={{ padding: '5pt 4pt' }}>Pavadinimas / aprašymas</th>
              {showUnitColumn && (
                <th
                  className="font-semibold text-left cursor-pointer hover:opacity-70"
                  style={{ padding: '5pt 4pt' }}
                  onClick={() => apply({ showUnit: false })}
                  title={t('invoiceTemplate.hideUnit')}
                >
                  Mat. vnt. ⨯
                </th>
              )}
              <th className="font-semibold text-right" style={{ padding: '5pt 4pt' }}>Kiekis</th>
              <th className="font-semibold text-right" style={{ padding: '5pt 4pt' }}>Kaina</th>
              <th className="font-semibold text-right" style={{ padding: '5pt 4pt' }}>PVM %</th>
              <th className="font-semibold text-right" style={{ padding: '5pt 4pt' }}>Suma</th>
              <th className="font-semibold text-right" style={{ padding: '5pt 4pt' }}>PVM suma</th>
            </tr>
          </thead>
          <tbody>
            {sampleLines.map((l, i) => (
              <tr key={i} style={{ borderBottom: '1pt solid #EEE' }}>
                <td className="text-right" style={{ padding: '5pt 4pt' }}>{i + 1}</td>
                <td style={{ padding: '5pt 4pt' }}>{l.name}</td>
                {showUnitColumn && <td style={{ padding: '5pt 4pt' }}>{l.unit}</td>}
                <td className="text-right" style={{ padding: '5pt 4pt' }}>{l.qty}</td>
                <td className="text-right" style={{ padding: '5pt 4pt' }}>{l.price}</td>
                <td className="text-right" style={{ padding: '5pt 4pt' }}>{l.vat}</td>
                <td className="text-right" style={{ padding: '5pt 4pt' }}>{l.sum}</td>
                <td className="text-right" style={{ padding: '5pt 4pt' }}>{l.sumVat}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!showUnitColumn && (
          <button
            type="button"
            className="mt-1 text-xs text-gray-400 hover:text-primary-600 underline"
            onClick={() => apply({ showUnit: true })}
          >
            + {t('invoiceTemplate.showUnit')}
          </button>
        )}

        {/* Totals per preset — mirrors the PDF compose branches. */}
        <div className="flex justify-end" style={{ paddingTop: '10pt' }}>
          {preset === 'minimal' ? (
            <div className="text-right">
              <div className="text-gray-500" style={{ fontSize: '9pt' }}>
                Iš viso be PVM 300,00&nbsp;&nbsp;·&nbsp;&nbsp;PVM 63,00 EUR
              </div>
              <div className="font-semibold" style={{ fontSize: '11pt', marginTop: '2pt' }}>
                Iš viso mokėti: 363,00 EUR
              </div>
            </div>
          ) : preset === 'modern' ? (
            <div className="text-right" style={{ width: '240pt' }}>
              <div className="text-gray-500">Iš viso be PVM: 300,00 EUR</div>
              <div className="text-gray-500">PVM iš viso: 63,00 EUR</div>
              <div
                className="font-semibold cursor-pointer hover:opacity-80"
                style={{ color: accentColor, fontSize: '13pt', marginTop: '4pt' }}
                title={t('invoiceTemplate.accentColor')}
              >
                Iš viso mokėti: 363,00 EUR
              </div>
            </div>
          ) : (
            <div className="text-right" style={{ width: '240pt' }}>
              <div className="text-gray-500">Iš viso be PVM: 300,00 EUR</div>
              <div className="text-gray-500">PVM iš viso: 63,00 EUR</div>
              <div
                className="font-semibold cursor-pointer hover:opacity-80"
                style={{ color: accentColor, fontSize: '13pt', marginTop: '4pt' }}
                title={t('invoiceTemplate.accentColor')}
              >
                Iš viso mokėti: 363,00 EUR
              </div>
            </div>
          )}
        </div>

        <div className="text-gray-600" style={{ marginTop: '6pt' }}>Apmokėti iki {new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10)}</div>

        {/* Page footer — pinned to the sheet bottom (PDF page.Footer): the
            minimal preset's small logo + the template footer text left, page
            numbers right. 9pt grey. Click the text to edit inline. */}
        <div className="mt-auto flex items-end justify-between gap-3" style={{ paddingTop: '6pt', fontSize: '9pt' }}>
          <div className="flex items-end gap-3 min-w-0">
            {preset === 'minimal' && hasLogo && logoSrc && (
              <img src={logoSrc} alt="logo" style={{ height: '20pt' }} />
            )}
            {footerEditing ? (
              <input
                autoFocus
                type="text"
                className="w-full border border-primary-300 rounded px-2 py-1"
                style={{ fontSize: '9pt' }}
                value={footerText}
                onChange={e => setFooterText(e.target.value)}
                onBlur={commitFooter}
                onKeyDown={e => { if (e.key === 'Enter') commitFooter(); }}
                placeholder={t('invoiceTemplate.footerPlaceholder')}
              />
            ) : (
              <div
                className="cursor-pointer text-gray-400 hover:text-gray-600 truncate"
                onClick={() => setFooterEditing(true)}
                title={t('invoiceTemplate.footerText')}
              >
                {footerText || t('invoiceTemplate.footerAdd')}
              </div>
            )}
          </div>
          <div className="text-gray-500 whitespace-nowrap">Puslapis 1 iš 1</div>
        </div>
        </div>
      </div>
    </div>
  );
}
